import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, PlugZap, RefreshCw } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }) : "mai");
const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiato"); };
const Box = ({ title, children, testId }) => (
    <div className="bg-white border border-neutral-200 rounded-md p-5 space-y-3" data-testid={testId}>
        <div className="font-semibold text-sm">{title}</div>{children}
    </div>
);

function TokenBox({ st, reload }) {
    const [raw, setRaw] = useState(null);
    const gen = async () => {
        if (st.token && !window.confirm("Il token attuale smetterà di funzionare. Generarne uno nuovo?")) return;
        try { const { data } = await api.post("/claude-connector/token"); setRaw(data.token); reload(); } catch (e) { toast.error(extractError(e)); }
    };
    const revoke = async () => {
        if (!window.confirm("Revocare il token? Claude non potrà più leggere i dati con questo token.")) return;
        await api.delete("/claude-connector/token"); setRaw(null); reload(); toast.success("Token revocato");
    };
    return (
        <Box title="Token di studio" testId="claude-token-box">
            <div className="text-xs text-neutral-500">Un solo token per l'account Claude dello studio, non legato a una persona. Viene mostrato per intero una sola volta; generarne uno nuovo disattiva il precedente.</div>
            {raw && (
                <div className="border border-amber-300 bg-amber-50 rounded p-3 space-y-2" data-testid="claude-token-raw-box">
                    <div className="text-xs font-medium text-amber-900">Copialo ora: non sarà più visibile.</div>
                    <div className="flex gap-2 items-center">
                        <code className="text-xs bg-white border border-amber-200 rounded px-2 py-1 flex-1 break-all" data-testid="claude-token-raw">{raw}</code>
                        <button onClick={() => copy(raw)} data-testid="claude-token-copy" className="text-xs inline-flex items-center gap-1 text-neutral-700"><Copy size={12} /> Copia</button>
                    </div>
                </div>
            )}
            {st.token ? (
                <div className="text-xs text-neutral-600" data-testid="claude-token-meta">Token attivo <code>{st.token.prefix}…</code> · generato il {fmt(st.token.created_at)}{st.token.created_by ? ` da ${st.token.created_by}` : ""} · ultimo utilizzo: {fmt(st.token.last_used_at)}</div>
            ) : <div className="text-xs text-neutral-500" data-testid="claude-token-none">Nessun token attivo.</div>}
            {st.is_admin ? <div className="flex gap-2">
                <button onClick={gen} data-testid="claude-token-generate" className="text-xs px-3 py-1.5 rounded-md bg-neutral-900 text-white inline-flex items-center gap-1.5"><KeyRound size={12} /> {st.token ? "Revoca e genera nuovo" : "Genera token"}</button>
                {st.token && <button onClick={revoke} data-testid="claude-token-revoke" className="text-xs px-3 py-1.5 rounded-md border border-neutral-200 hover:text-red-600">Revoca</button>}
            </div> : <div className="text-xs text-neutral-400">Solo un amministratore può generare o revocare il token.</div>}
        </Box>
    );
}

function Guide({ url }) {
    return (
        <Box title="Come collegare Claude, passo passo" testId="claude-guide">
            <ol className="text-sm text-neutral-700 list-decimal pl-5 space-y-1.5">
                <li>Un amministratore genera qui sopra il token di studio e lo copia.</li>
                <li>Apri Claude con l'account dello studio (claude.ai o l'app) e vai in <b>Impostazioni → Connettori</b>.</li>
                <li>Premi <b>Aggiungi connettore personalizzato</b>.</li>
                <li>Come nome scrivi "jester" e come <b>URL del server MCP</b> incolla: <code className="text-xs bg-neutral-50 px-1">{url}</code></li>
                <li>Come autenticazione scegli <b>Nessun accesso</b> (No sign-in), poi apri la sezione <b>Request headers</b>.</li>
                <li>Aggiungi l'intestazione <b>authorization</b> con valore <code className="text-xs bg-neutral-50 px-1">Bearer </code> seguito dal token (es. <code className="text-xs bg-neutral-50 px-1">Bearer jst_…</code>).</li>
                <li>Salva. In una nuova chat attiva il connettore "jester" e chiedi per esempio: "mostrami la scheda del cliente Rossi" o "a che punto è il progetto X?".</li>
            </ol>
            <div className="text-xs text-neutral-500">Se non vedi la sezione "Request headers", la tua organizzazione Claude non ha ancora accesso a questa funzione (è in prova): scrivimelo e prepariamo un'alternativa.</div>
        </Box>
    );
}

const METHODS = { initialize: "Connessione", "tools/list": "Elenco funzioni", "tools/call": "Funzione", ping: "Ping", "notifications/initialized": "Connessione confermata" };
const argsText = (a) => Object.entries(a || {}).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ");

function Logs({ logs }) {
    const [all, setAll] = useState(false);
    const shown = all ? logs : logs.filter((l) => l.method === "tools/call" || !l.ok);
    return (
        <Box title="Registro degli accessi" testId="claude-logs">
            <div className="flex items-center justify-between text-xs text-neutral-500">
                <span>Ogni richiesta ricevuta, anche quelle rifiutate (ultime 200).</span>
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} data-testid="claude-logs-all" /> mostra anche connessioni ed elenco funzioni</label>
            </div>
            {shown.length === 0 ? <div className="text-xs text-neutral-500">Nessuna chiamata ricevuta finora.</div> : (
                <table className="w-full text-xs">
                    <thead><tr className="text-left text-neutral-500"><th className="py-1">Data e ora</th><th>Da</th><th>Richiesta</th><th>Dettagli</th><th>Esito</th></tr></thead>
                    <tbody>{shown.map((l, i) => (
                        <tr key={i} className="border-t border-neutral-100 align-top" data-testid={`claude-log-${i}`}>
                            <td className="py-1.5 whitespace-nowrap pr-2">{fmt(l.at)}</td>
                            <td className="pr-2" title={`${l.ip || ""} ${l.user_agent || ""}`}>{l.source}{l.token_prefix ? ` (${l.token_prefix}…)` : ""}</td>
                            <td className="pr-2">{l.tool ? <code>{l.tool}</code> : METHODS[l.method] || l.method || "—"}</td>
                            <td className="pr-2 text-neutral-500 break-all">{argsText(l.arguments)}</td>
                            <td className={l.ok ? "text-green-700" : "text-red-600"}>{l.ok ? "ok" : l.error || "errore"}</td>
                        </tr>))}</tbody>
                </table>
            )}
        </Box>
    );
}

export function ClaudeConnector() {
    const [st, setSt] = useState(null);
    const [logs, setLogs] = useState([]);
    const [test, setTest] = useState(null);
    const load = async () => {
        try { setSt((await api.get("/claude-connector")).data); setLogs((await api.get("/claude-connector/logs")).data); }
        catch (e) { toast.error(extractError(e)); }
    };
    useEffect(() => { load(); }, []);
    if (!st) return null;
    const toggle = async (v) => { try { await api.patch("/claude-connector", { enabled: v }); load(); } catch (e) { toast.error(extractError(e)); } };
    const runTest = async () => { setTest({ loading: true }); try { setTest((await api.post("/claude-connector/test")).data); } catch (e) { setTest({ ok: false, message: extractError(e) }); } };
    return (
        <div className="space-y-3 max-w-3xl" data-testid="claude-connector">
            <Box title="Connettore per Claude (sola lettura)" testId="claude-main">
                <div className="flex items-center justify-between gap-4">
                    <div className="text-xs text-neutral-500">Permette a Claude di consultare clienti, progetti, task, produzione e Time Machine. Non può creare, modificare o cancellare nulla.</div>
                    <label className="flex items-center gap-2 text-xs text-neutral-600 shrink-0">
                        <Switch checked={st.enabled} disabled={!st.is_admin} onCheckedChange={toggle} data-testid="claude-enabled-toggle" />
                        <span data-testid="claude-enabled-label">{st.enabled ? "Attivo" : "Disattivato"}</span>
                    </label>
                </div>
                {!st.is_admin && <div className="text-xs text-neutral-400">Solo un amministratore può attivarlo o disattivarlo.</div>}
                <div className="text-xs text-neutral-600">Indirizzo del server MCP:</div>
                <div className="flex items-center gap-2">
                    <code className="text-xs bg-neutral-50 border border-neutral-200 rounded px-2 py-1 flex-1 truncate" data-testid="claude-server-url">{st.server_url}</code>
                    <button onClick={() => copy(st.server_url)} data-testid="claude-server-url-copy" className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"><Copy size={12} /> Copia</button>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={runTest} disabled={test?.loading} data-testid="claude-test-btn" className="text-xs px-3 py-1.5 rounded-md border border-neutral-200 hover:bg-neutral-50 inline-flex items-center gap-1.5"><PlugZap size={12} /> {test?.loading ? "Verifica…" : "Verifica connessione"}</button>
                    {test && !test.loading && <span className={`text-xs ${test.ok ? "text-green-700" : "text-red-600"}`} data-testid="claude-test-result">{test.message}</span>}
                    <button onClick={load} className="ml-auto text-xs text-neutral-400 hover:text-neutral-700 inline-flex items-center gap-1" data-testid="claude-refresh"><RefreshCw size={12} /> Aggiorna</button>
                </div>
            </Box>
            <TokenBox st={st} reload={load} />
            <Guide url={st.server_url} />
            <Logs logs={logs} />
        </div>
    );
}
